import { useState } from 'react';
import { StudentBilling } from './useFinanceData';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export interface ExportConfig {
  filename: string;
  headers: string[];
  data: any[];
  format: 'csv' | 'excel';
}

export interface DebtorData {
  id: string;
  student_name: string;
  student_class: string;
  activity_name: string;
  total_amount: number;
  due_date: string;
  days_overdue: number;
  status: string;
}

export const useExportData = () => {
  const [isExporting, setIsExporting] = useState(false);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('es-ES');
  };

  const downloadCSV = (config: ExportConfig) => {
    const csvContent = [
      config.headers.join(','),
      ...config.data.map(row => 
        config.headers.map(header => {
          const value = row[header] || '';
          // Escapar comillas dobles y envolver en comillas si contiene comas
          return typeof value === 'string' && value.includes(',') 
            ? `"${value.replace(/"/g, '""')}"` 
            : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${config.filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportInvoices = async (billing: StudentBilling[], filename?: string) => {
    setIsExporting(true);
    try {
      const headers = [
        'Estudiante',
        'Clase',
        'Actividad',
        'Sesiones',
        'Precio por Día',
        'Importe Total',
        'Estado',
        'Fecha Factura',
        'Fecha Vencimiento',
        'Fecha Pago',
        'Notas'
      ];

      const data = billing.map(bill => ({
        'Estudiante': bill.student?.name || '',
        'Clase': bill.student?.class || '',
        'Actividad': bill.activity?.name || '',
        'Sesiones': bill.sessions_attended,
        'Precio por Día': `${bill.price_amount.toFixed(2)}€`,
        'Importe Total': `${bill.total_amount.toFixed(2)}€`,
        'Estado': bill.status === 'paid' ? 'Pagado' : 
                 bill.status === 'pending' ? 'Pendiente' : 
                 bill.status === 'overdue' ? 'Vencido' : 'Cancelado',
        'Fecha Factura': formatDate(bill.invoice_date),
        'Fecha Vencimiento': formatDate(bill.due_date),
        'Fecha Pago': bill.paid_date ? formatDate(bill.paid_date) : '',
        'Notas': bill.notes || ''
      }));

      const defaultFilename = `facturas_${new Date().toISOString().split('T')[0]}`;
      
      downloadCSV({
        filename: filename || defaultFilename,
        headers,
        data,
        format: 'csv'
      });

      toast.success(`Facturas exportadas correctamente (${data.length} registros)`);
    } catch (error) {
      toast.error('Error al exportar las facturas');
    } finally {
      setIsExporting(false);
    }
  };

  const exportDebtors = async (debtors: DebtorData[], filename?: string) => {
    setIsExporting(true);
    try {
      const headers = [
        'Estudiante',
        'Clase',
        'Actividad',
        'Importe Adeudado',
        'Fecha Vencimiento',
        'Días Vencido',
        'Estado'
      ];

      const data = debtors.map(debtor => ({
        'Estudiante': debtor.student_name,
        'Clase': debtor.student_class,
        'Actividad': debtor.activity_name,
        'Importe Adeudado': `${debtor.total_amount.toFixed(2)}€`,
        'Fecha Vencimiento': formatDate(debtor.due_date),
        'Días Vencido': debtor.days_overdue,
        'Estado': debtor.status === 'pending' ? 'Pendiente' : 'Vencido'
      }));

      const defaultFilename = `deudores_${new Date().toISOString().split('T')[0]}`;
      
      downloadCSV({
        filename: filename || defaultFilename,
        headers,
        data,
        format: 'csv'
      });

      toast.success(`Deudores exportados correctamente (${data.length} registros)`);
    } catch (error) {
      toast.error('Error al exportar los deudores');
    } finally {
      setIsExporting(false);
    }
  };

  const exportFinancialSummary = async (summaryData: any, filename?: string) => {
    setIsExporting(true);
    try {
      const headers = [
        'Métrica',
        'Valor',
        'Fecha Generación'
      ];

      const data = [
        {
          'Métrica': 'Total Facturado',
          'Valor': `${summaryData.totalBilled?.toFixed(2) || '0.00'}€`,
          'Fecha Generación': formatDate(new Date().toISOString())
        },
        {
          'Métrica': 'Total Cobrado',
          'Valor': `${summaryData.totalPaid?.toFixed(2) || '0.00'}€`,
          'Fecha Generación': formatDate(new Date().toISOString())
        },
        {
          'Métrica': 'Pendiente de Cobro',
          'Valor': `${summaryData.totalPending?.toFixed(2) || '0.00'}€`,
          'Fecha Generación': formatDate(new Date().toISOString())
        },
        {
          'Métrica': 'Número de Facturas',
          'Valor': summaryData.totalInvoices || '0',
          'Fecha Generación': formatDate(new Date().toISOString())
        },
        {
          'Métrica': 'Número de Deudores',
          'Valor': summaryData.totalDebtors || '0',
          'Fecha Generación': formatDate(new Date().toISOString())
        }
      ];

      const defaultFilename = `resumen_financiero_${new Date().toISOString().split('T')[0]}`;
      
      downloadCSV({
        filename: filename || defaultFilename,
        headers,
        data,
        format: 'csv'
      });

      toast.success('Resumen financiero exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar el resumen financiero');
    } finally {
      setIsExporting(false);
    }
  };

  const exportReportData = async (reportData: any, filename?: string) => {
    setIsExporting(true);
    try {
      const headers = [
        'Período',
        'Tipo de Reporte',
        'Fecha Generación',
        'Total Ingresos',
        'Total Estudiantes',
        'Ocupación Media',
        'Actividad Más Rentable'
      ];

      const data = [{
        'Período': reportData.period || 'No especificado',
        'Tipo de Reporte': reportData.type || 'financial_report',
        'Fecha Generación': formatDate(new Date().toISOString()),
        'Total Ingresos': reportData.totalRevenue ? `${reportData.totalRevenue.toFixed(2)}€` : 'N/A',
        'Total Estudiantes': reportData.totalStudents || 'N/A',
        'Ocupación Media': reportData.avgOccupancy ? `${reportData.avgOccupancy.toFixed(1)}%` : 'N/A',
        'Actividad Más Rentable': reportData.mostProfitable || 'N/A'
      }];

      const defaultFilename = `reporte_${reportData.period || 'general'}_${new Date().toISOString().split('T')[0]}`;
      
      downloadCSV({
        filename: filename || defaultFilename,
        headers,
        data,
        format: 'csv'
      });

      toast.success('Reporte exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar el reporte');
    } finally {
      setIsExporting(false);
    }
  };

  const exportStudents = async (filename?: string) => {
    setIsExporting(true);
    try {
      
      // Obtener datos completos de estudiantes con sus actividades y horarios
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select(`
          id,
          name,
          first_name,
          last_name,
          class,
          email,
          phone,
          default_billing_type,
          student_activities!inner (
            id,
            status,
            billing_type_override,
            activity_id,
            activities!inner (
              id,
              name,
              start_time,
              end_time,
              requires_day_selection
            )
          )
        `)
        .eq('student_activities.status', 'active')
        .order('name');

      if (studentsError) throw studentsError;

      

      // Obtener horarios para todas las actividades que requieren selección de días
      const activityIds = studentsData?.flatMap(student => 
        student.student_activities?.map(sa => sa.activity_id) || []
      ) || [];

      const { data: schedulesData, error: schedulesError } = await supabase
        .from('student_activity_schedule')
        .select('student_id, activity_id, day_of_week')
        .in('activity_id', activityIds);

      if (schedulesError) throw schedulesError;

      // Agrupar horarios por estudiante y actividad
      const schedulesByStudentActivity: Record<string, number[]> = {};
      schedulesData?.forEach(schedule => {
        const key = `${schedule.student_id}-${schedule.activity_id}`;
        if (!schedulesByStudentActivity[key]) {
          schedulesByStudentActivity[key] = [];
        }
        schedulesByStudentActivity[key].push(schedule.day_of_week);
      });

      const headers = [
        'Nombre',
        'Apellidos',
        'Clase',
        'Email',
        'Teléfono',
        'Actividad',
        'Horario General',
        'Días Programados',
        'Estado Inscripción',
        'Tipo Facturación'
      ];

      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

      const data: any[] = [];

      studentsData?.forEach(student => {
        if (student.student_activities && student.student_activities.length > 0) {
          student.student_activities.forEach(sa => {
            const activity = sa.activities;
            if (!activity) return;

            // Obtener días programados para esta combinación estudiante-actividad
            const scheduleKey = `${student.id}-${sa.activity_id}`;
            const scheduledDays = schedulesByStudentActivity[scheduleKey] || [];
            
            // Formatear días programados
            let daysText = '';
            if (activity.requires_day_selection && scheduledDays.length > 0) {
              daysText = scheduledDays
                .sort((a, b) => a - b)
                .map(day => dayNames[day])
                .join(', ');
            } else if (!activity.requires_day_selection) {
              // Usar los días generales de la actividad
              daysText = activity.requires_day_selection 
                ? 'Selección personalizada requerida'
                : 'Según horario general';
            } else {
              daysText = 'Sin días asignados';
            }

            // Determinar tipo de facturación
            const billingType = sa.billing_type_override || student.default_billing_type;
            const billingTypeText = billingType === 'per_session' ? 'Por Sesión' : 'Mensual';

            // Formatear horario general
            const generalSchedule = `${activity.start_time} - ${activity.end_time}`;

            data.push({
              'Nombre': student.first_name || student.name.split(' ')[0] || '',
              'Apellidos': student.last_name || student.name.split(' ').slice(1).join(' ') || '',
              'Clase': student.class,
              'Email': student.email || '',
              'Teléfono': student.phone || '',
              'Actividad': activity.name,
              'Horario General': generalSchedule,
              'Días Programados': daysText,
              'Estado Inscripción': sa.status === 'active' ? 'Activo' : 'Inactivo',
              'Tipo Facturación': billingTypeText
            });
          });
        } else {
          // Estudiante sin actividades
          data.push({
            'Nombre': student.first_name || student.name.split(' ')[0] || '',
            'Apellidos': student.last_name || student.name.split(' ').slice(1).join(' ') || '',
            'Clase': student.class,
            'Email': student.email || '',
            'Teléfono': student.phone || '',
            'Actividad': 'Sin actividades',
            'Horario General': '-',
            'Días Programados': '-',
            'Estado Inscripción': '-',
            'Tipo Facturación': student.default_billing_type === 'per_session' ? 'Por Sesión' : 'Mensual'
          });
        }
      });

      const defaultFilename = `estudiantes_${new Date().toISOString().split('T')[0]}`;
      
      downloadCSV({
        filename: filename || defaultFilename,
        headers,
        data,
        format: 'csv'
      });

      toast.success(`Estudiantes exportados correctamente (${data.length} registros)`);
    } catch (error) {
      toast.error('Error al exportar los estudiantes');
    } finally {
      setIsExporting(false);
    }
  };

  return {
    isExporting,
    exportInvoices,
    exportDebtors,
    exportFinancialSummary,
    exportReportData,
    exportStudents
  };
};